import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import games from "../../data/games";
import { getInspectableTotals } from "../../data/inspectables";
import { makeMockStats, applyRealMetricsToStats } from "../../games/origami/config/scoreboardStats";
import { fmtTime, clamp, getTimeLimitSec, getBestRealisticSec, getPointsBreakdownFromDB } from "../../lib/scoreboardUtils";

const API = process.env.REACT_APP_API_URL;

const dicebearUrl = (seed) =>
  `https://api.dicebear.com/7.x/adventurer/svg?seed=${encodeURIComponent(seed)}`;

function gameNameById(id) {
  const g = games?.find((x) => Number(x.id) === Number(id));
  return g?.name || `Jeu #${id}`;
}

function gameIsBetaById(id) {
  const g = games?.find((x) => Number(x.id) === Number(id));
  return Boolean(g?.isBeta);
}

function teamLabel(teamName) {
  const t = (teamName || "").trim();
  return t ? t : "Équipe sans nom";
}

export default function Scoreboard({ getGameStats }) {
  const { code } = useParams();

  const [data, setData] = useState(null);
  const [metrics, setMetrics] = useState(null);
  const [err, setErr] = useState(null);

  const [tQuote, setTQuote] = useState("");
  const [tName, setTName] = useState("");
  const [tOrg, setTOrg] = useState("");
  const [tTag, setTTag] = useState("Team building");
  const [tSent, setTSent] = useState(false);
  const [tSending, setTSending] = useState(false);
  const [tError, setTError] = useState(null);
  const [tRating, setTRating] = useState(0);

  const [betaBannerClosed, setBetaBannerClosed] = useState(false);

  const playerCount = data?.players?.length || 0;
  const gameId = data?.gameId;
  const inspectable = useMemo(() => {
    if (!gameId || !playerCount) return { teamTotal: 0, bySeat: {} };

    return getInspectableTotals({
      gameId,
      playerCount,
      stage: 1,
    });
  }, [gameId, playerCount]);

  useEffect(() => {
    let cancelled = false;
    let intervalId = null;

    setErr(null);
    setData(null);
    setMetrics(null);

    async function loadScoreboard() {
      const sb = await axios.get(`${API}/api/sessions/${code}/scoreboard`);
      if (cancelled) return;
      setData(sb.data);
      return sb.data;
    }

    async function loadMetrics() {
      const m = await axios.get(`${API}/api/metrics`, {
        params: { sessionCode: code },
      });
      if (cancelled) return;
      setMetrics(m.data);
      return m.data;
    }

    async function load() {
      try {
        const sbData = await loadScoreboard();
        await loadMetrics();

        // polling seulement si la session n'est pas finie
        if (sbData?.status !== "used") {
          intervalId = setInterval(() => {
            loadMetrics().catch(() => { });
          }, 2000);
        }
      } catch (e) {
        if (cancelled) return;
        setErr(e.response?.data?.message || "Erreur chargement scoreboard");
      }
    }

    load();

    return () => {
      cancelled = true;
      if (intervalId) clearInterval(intervalId);
    };
  }, [code]);

  const stats = useMemo(() => {
    if (!data) return null;

    const base = makeMockStats({
      code: data.code,
      players: data.players || [],
      playedDuration: data.playedDuration || 0,
    });

    return applyRealMetricsToStats(base, metrics);
  }, [data, metrics]);

  if (err) return <div className="p-6 text-rose-300">❌ {err}</div>;
  if (!data || !stats) return <div className="p-6 text-slate-200">Chargement…</div>;

  const playedDuration = data?.playedDuration || 0;
  const mvp = stats.ranking?.[0] || null;
  const mentions = stats.ranking.slice(1, 3); // 2e & 3e (optionnel)

  const gameName = gameNameById(data.gameId);
  const isBetaGame = gameIsBetaById(data.gameId);
  const teamName = teamLabel(data.teamName);




  // Points officiels DB
  const points = Number(data?.points ?? 0) || 0;

  // Breakdown (DB si dispo, sinon fallback)
  const breakdown = getPointsBreakdownFromDB(data, stats);

  const timeLimitSec = breakdown.timeLimitSec || getTimeLimitSec(data.gameId);
  const playedSec = breakdown.playedDurationSec || playedDuration;

  const bestSec = getBestRealisticSec(data.gameId, timeLimitSec);

  // 0% si playedSec >= timeLimitSec
  // 100% si playedSec <= bestSec
  const effTime = clamp(
    (timeLimitSec - playedSec) / Math.max(1, timeLimitSec - bestSec),
    0,
    1
  );

  const effTimePct = Math.round(effTime * 100);
  const effTimeWidth = clamp(effTimePct, 0, 100);

  const timeScore = breakdown.timeScore;
  const penaltyErrors = breakdown.penaltyErrors;

  // on groupe "aides" = indices + réponses
  const penaltyHints = (breakdown.penaltyHints || 0) + (breakdown.penaltyAnswers || 0);

  async function submitTestimonial() {
    setTError(null);

    if (!tQuote.trim() || tQuote.trim().length < 10) {
      setTError("Écris au moins 10 caractères 🙂");
      return;
    }

    setTSending(true);
    try {
      await axios.post(`${API}/api/testimonials`, {
        sessionCode: code,
        quote: tQuote.trim(),
        firstName: tName.trim(),
        companyName: tOrg.trim(),
        tag: tTag,
        rating: tRating,
      });

      setTSent(true);
    } catch (e) {
      setTError(e.response?.data?.message || "Impossible d’envoyer le témoignage.");
    } finally {
      setTSending(false);
    }
  }

  const canSend =
    !tSending &&
    tRating >= 1 &&
    tName.trim().length > 0 &&
    tOrg.trim().length > 0 &&
    tQuote.trim().length >= 10;


  const gameStats = getGameStats ? getGameStats(stats, breakdown, data) : null;

  return (
    <div className="min-h-[calc(100vh-85px)] bg-[#070712] text-white">
      <div className="pointer-events-none fixed inset-0 opacity-60">
        <div className="absolute -top-32 left-1/2 h-[520px] w-[520px] -translate-x-1/2 rounded-full blur-3xl bg-fuchsia-600/20" />
        <div className="absolute top-40 -left-20 h-[420px] w-[420px] rounded-full blur-3xl bg-cyan-500/15" />
        <div className="absolute bottom-0 right-0 h-[520px] w-[520px] rounded-full blur-3xl bg-indigo-500/15" />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 py-10">
        {isBetaGame && !betaBannerClosed && process.env.REACT_APP_BETA_MODE === "true" && (
          <div className="mb-6 rounded-2xl border border-cyan-400/25 bg-cyan-500/10 px-4 py-3 flex items-start justify-between gap-4">
            <div className="min-w-0">
              <p className="text-sm font-semibold text-cyan-100">
                Merci d'avoir participé à la bêta 🙌
              </p>
              <p className="text-[12px] text-slate-200 mt-1">
                Vos retours nous aideront énormément à améliorer l’expérience. N'hésitez pas à laisser un témoignage via le formulaire présent sur cette page. Merci !
              </p>
            </div>

            <button
              type="button"
              onClick={() => setBetaBannerClosed(true)}
              className="flex-shrink-0 rounded-xl border border-white/10 bg-black/20 px-3 py-2 text-[12px] text-slate-200 hover:bg-white/5 transition"
              aria-label="Fermer la notification bêta"
            >
              ✕
            </button>
          </div>
        )}

        <div className="flex flex-col gap-2 md:flex-row md:items-end md:justify-between">
          <div>
            <p className="text-[11px] uppercase tracking-[0.28em] text-slate-400">
              Debrief de session
            </p>

            <h1 className="text-3xl md:text-4xl font-extrabold tracking-tight">
              <span className="text-slate-200">Scoreboard — </span>
              <span className="text-fuchsia-300">{teamName}</span>
            </h1>

            <p className="mt-2 text-sm text-slate-300">
              <span className="text-slate-500">{gameName}</span>
              <span className="text-slate-500"> · </span>
              <span className="text-slate-500">Code session</span>{" "}
              <span className="font-extrabold text-slate-200">{data.code}</span>
            </p>

            <p className="mt-2 text-sm text-slate-300">
              Temps total joué :{" "}
              <span className="font-extrabold text-cyan-200 whitespace-nowrap">
                {fmtTime(playedDuration)}
              </span>
            </p>
          </div>

          <div className="mt-4 md:mt-0 flex items-center gap-2">
            <Link
              to="/scoreboards"
              className="rounded-2xl px-4 py-2 text-sm font-semibold bg-white/5 border border-white/10 hover:bg-white/10 transition"
            >
              ← Retour leaderboard
            </Link>
          </div>
        </div>

        <div className="mt-8 grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* main */}
          <section className="lg:col-span-8 space-y-6">
            {/* Points */}
            <div className="rounded-3xl border border-white/10 bg-white/5 backdrop-blur p-5">
              <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                <div className="min-w-0">
                  <p className="text-[11px] uppercase tracking-[0.22em] text-slate-400">
                    Points
                  </p>

                  <div className="mt-2 flex items-end gap-3">
                    <p className="text-4xl md:text-5xl font-extrabold text-emerald-200 leading-none">
                      {points}
                    </p>
                    <div className="pb-1">


                    </div>
                  </div>

                  {/* Progress bar */}
                  <div className="mt-4">
                    <div className="flex items-center justify-between text-[11px] text-slate-400">
                      <span>Efficacité</span>
                      <span className="font-semibold text-slate-300">
                        {effTimePct}%
                      </span>
                    </div>

                    <div className="mt-2 h-2 rounded-full bg-black/30 border border-white/10 overflow-hidden">
                      <div
                        className="h-full bg-gradient-to-r from-emerald-500/70 to-cyan-500/60"
                        style={{ width: `${effTimeWidth}%` }}
                      />
                    </div>
                    <p className="mt-2 text-[11px] text-slate-500">
                      Temps joué: <span className="text-slate-300 font-semibold">{fmtTime(playedSec)}</span>
                      {" "}· Limite: <span className="text-slate-300 font-semibold">{fmtTime(timeLimitSec)}</span>
                      {" "}· 100% à: <span className="text-slate-300 font-semibold">{fmtTime(bestSec)}</span>
                    </p>
                  </div>
                </div>

                {/* Right: breakdown mini-cards */}
                <div className="w-full md:w-[420px]">
                  <div className="grid grid-cols-2 gap-3">
                    <div className="rounded-2xl border border-white/10 bg-black/30 p-3">
                      <p className="text-[11px] uppercase tracking-[0.18em] text-slate-400">
                        Score de base
                      </p>
                      <p className="mt-1 text-xl font-extrabold text-cyan-200">
                        {breakdown.base}
                      </p>
                      <p className="text-[11px] text-slate-500 mt-1">
                        L'important c'est de s'amuser
                      </p>
                    </div>
                    <div className="rounded-2xl border border-white/10 bg-black/30 p-3">
                      <p className="text-[11px] uppercase tracking-[0.18em] text-slate-400">
                        Bonus finisseur
                      </p>
                      <p className="mt-1 text-xl font-extrabold text-cyan-200">
                        {breakdown.finishBonus}
                      </p>
                      <p className="text-[11px] text-slate-500 mt-1">
                        Validez toutes les étapes
                      </p>
                    </div>

                    <div className="rounded-2xl border border-white/10 bg-black/30 p-3">
                      <p className="text-[11px] uppercase tracking-[0.18em] text-slate-400">
                        Score temps
                      </p>
                      <p className="mt-1 text-xl font-extrabold text-cyan-200">
                        {timeScore}
                      </p>
                      <p className="text-[11px] text-slate-500 mt-1">
                        Dépend du temps restant
                      </p>
                    </div>

                    <div className="rounded-2xl border border-white/10 bg-black/30 p-3">
                      <p className="text-[11px] uppercase tracking-[0.18em] text-slate-400">
                        Score inspection
                      </p>
                      <p className="mt-1 text-xl font-extrabold text-cyan-200">
                        {breakdown.bonusInteractions}
                      </p>
                      <p className="text-[11px] text-slate-500 mt-1">
                        {inspectable.teamTotal
                          ? `${stats.totals.interactionsTotal}/${inspectable.teamTotal} éléments inspectés`
                          : "Total inspectable indisponible"}
                      </p>
                    </div>
                    <div className="rounded-2xl border border-white/10 bg-black/30 p-3">
                      <p className="text-[11px] uppercase tracking-[0.18em] text-slate-400">
                        Pénalité erreurs
                      </p>
                      <p className="mt-1 text-xl font-extrabold text-rose-200">
                        −{penaltyErrors}
                      </p>
                      <p className="text-[11px] text-slate-500 mt-1">
                        {stats.totals.wrongPwdTotal} × 120
                      </p>
                    </div>

                    <div className="rounded-2xl border border-white/10 bg-black/30 p-3">
                      <p className="text-[11px] uppercase tracking-[0.18em] text-slate-400">
                        Pénalité indices
                      </p>
                      <p className="mt-1 text-xl font-extrabold text-amber-200">
                        −{penaltyHints}
                      </p>
                      <p className="text-[11px] text-slate-500 mt-1">
                        Indices: −{breakdown.penaltyHints || 0} · Réponses: −
                        {breakdown.penaltyAnswers || 0}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* KPI row */}
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
              <div className="rounded-3xl border border-white/10 bg-white/5 backdrop-blur p-4">
                <p className="text-[11px] uppercase tracking-[0.22em] text-slate-400">
                  Indices utilisés
                </p>
                <p className="mt-2 text-2xl font-extrabold text-amber-200">
                  {stats.totals.hints.total}
                </p>
                <p className="text-xs text-slate-400 mt-1">Total équipe</p>
              </div>

              <div className="rounded-3xl border border-white/10 bg-white/5 backdrop-blur p-4">
                <p className="text-[11px] uppercase tracking-[0.22em] text-slate-400">
                  Réponses affichées
                </p>
                <p className="mt-2 text-2xl font-extrabold text-red-200 whitespace-nowrap">
                  {stats.totals.hints.answer}
                </p>
                <p className="text-xs text-slate-400 mt-1">Total équipe</p>
              </div>

              <div className="rounded-3xl border border-white/10 bg-white/5 backdrop-blur p-4">
                <p className="text-[11px] uppercase tracking-[0.22em] text-slate-400">
                  Mots de passe faux
                </p>
                <p className="mt-2 text-2xl font-extrabold text-fuchsia-200 whitespace-nowrap">
                  {stats.totals.wrongPwdTotal}
                </p>
                <p className="text-xs text-slate-400 mt-1">Total équipe</p>
              </div>

              <div className="rounded-3xl border border-white/10 bg-white/5 backdrop-blur p-4">
                <p className="text-[11px] uppercase tracking-[0.22em] text-slate-400">
                  Objets inspectés
                </p>
                <p className="mt-2 text-2xl font-extrabold text-cyan-200 whitespace-nowrap">
                  {stats.totals.interactionsTotal}
                </p>
                <p className="text-xs text-slate-400 mt-1">Total équipe</p>
              </div>
            </div>

            {/* MVP */}
            <div className="rounded-3xl border border-white/10 bg-white/5 backdrop-blur p-5">
              {!mvp ? (
                <div className="rounded-2xl border border-white/10 bg-black/30 p-4 text-sm text-slate-300">
                  Aucun MVP disponible.
                </div>
              ) : (
                <div
                  className="
                    relative overflow-hidden rounded-2xl border border-fuchsia-400/35
                    bg-black/30 p-5
                    shadow-[0_0_32px_rgba(217,70,239,0.20)]
                  "
                >
                  {/* halo léger */}
                  <div className="pointer-events-none absolute inset-0">
                    <div className="absolute -top-24 -left-24 h-64 w-64 rounded-full bg-fuchsia-500/15 blur-3xl" />
                    <div className="absolute -bottom-24 -right-24 h-64 w-64 rounded-full bg-cyan-500/10 blur-3xl" />
                  </div>

                  {/* badge ⭐ (ne masque jamais le pseudo) */}
                  <div
                    className="
                      absolute top-4 right-4
                      rounded-xl border border-amber-400/30 bg-amber-500/10
                      px-3 py-2 text-[11px] font-extrabold text-amber-200
                      shadow-[0_0_18px_rgba(251,191,36,0.16)]
                    "
                  >
                    ⭐ MVP #1
                  </div>

                  <div className="relative flex items-start gap-4 pr-24">
                    {/* avatar */}
                    <img
                      src={dicebearUrl(mvp.avatar || mvp.pseudo)}
                      alt={mvp.avatarLabel || "Avatar"}
                      className="h-14 w-14 rounded-2xl bg-white/5 border border-white/10 p-1 flex-shrink-0"
                    />

                    {/* contenu */}
                    <div className="min-w-0">
                      <p className="text-[11px] uppercase tracking-[0.22em] text-slate-400">
                        🥇 Agent de la session
                      </p>

                      {/* 👇 Ici : on évite truncate pour les pseudos. On wrap proprement. */}
                      <p className="mt-1 text-xl font-extrabold text-white leading-snug break-words">
                        {mvp.pseudo}
                      </p>

                      <p className="mt-2 text-[12px] text-slate-300">
                        Indices :{" "}
                        <span className="text-amber-200 font-extrabold">{mvp.hints.total}</span>{" "}
                        · Réponses :{" "}
                        <span className="text-red-200 font-extrabold">{mvp.hints.answer}</span>{" "}
                        · Erreurs :{" "}
                        <span className="text-rose-200 font-extrabold">{mvp.wrongPasswords}</span>{" "}
                        · Objets inspectés :{" "}
                        <span className="text-indigo-200 font-extrabold">{mvp.interactions}</span>
                      </p>

                      {/* Mentions (optionnel, mais ça garde l’info sans 3 grosses cards) */}
                      {mentions?.length > 0 && (
                        <div className="mt-4 flex flex-wrap gap-2">
                          {mentions.map((p, i) => (
                            <span
                              key={p.id}
                              className="inline-flex items-center gap-2 rounded-xl border border-white/10 bg-white/5 px-3 py-2 text-[12px] text-slate-200"
                            >
                              <span className="text-base">{i === 0 ? "🥈" : "🥉"}</span>
                              <span className="font-semibold break-words">{p.pseudo}</span>
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>


            {/* table players */}
            <div className="rounded-3xl border border-white/10 bg-white/5 backdrop-blur p-5">
              <div className="flex items-center justify-between">
                <p className="text-[11px] uppercase tracking-[0.22em] text-slate-400">
                  Détails par agent
                </p>
              </div>

              <div className="mt-4 space-y-2">
                {stats.perPlayer.map((p) => (
                  <div
                    key={p.id}
                    className="rounded-2xl border border-white/10 bg-black/30 px-4 py-3 flex items-center justify-between gap-4"
                  >
                    <div className="min-w-0">
                      <div className="flex flex-row item-center">
                        <img
                          src={dicebearUrl(p.avatar || p.pseudo)}
                          alt={p.avatarLabel || "Avatar"}
                          className="h-10 w-10 rounded-2xl bg-white/5 border border-white/10 p-1 mr-2"
                        />
                        <div className="flex flex-col min-w-0">
                          <p className="text-sm font-semibold truncate">
                            {p.pseudo}{" "}
                            <span className="text-[11px] text-slate-400">
                              · {p.avatarLabel ? `${p.avatarLabel}` : "Avatar"}
                            </span>
                          </p>

                          <p className="text-[11px] text-slate-400 truncate">
                            Objets inspectés:{" "}
                            <span className="font-bold text-indigo-200">
                              {p.interactions}
                            </span>
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center gap-3 flex-shrink-0">
                      <div className="rounded-xl border border-white/10 bg-white/5 px-3 py-2 text-[11px] whitespace-nowrap">
                        <span className="text-slate-400">Indices</span>{" "}
                        <span className="font-extrabold text-amber-200">
                          {p.hints.total}
                        </span>
                      </div>

                      <div className="rounded-xl border border-white/10 bg-white/5 px-3 py-2 text-[11px] whitespace-nowrap">
                        <span className="text-slate-400">Réponse</span>{" "}
                        <span className="font-extrabold text-red-200">
                          {p.hints.answer}
                        </span>
                      </div>

                      <div className="rounded-xl border border-white/10 bg-white/5 px-3 py-2 text-[11px] whitespace-nowrap">
                        <span className="text-slate-400">Erreurs</span>{" "}
                        <span className="font-extrabold text-rose-200">
                          {p.wrongPasswords}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* aside */}
          <aside className="lg:col-span-4 space-y-6">
            <div className="rounded-3xl border border-fuchsia-400/25 bg-gradient-to-b from-fuchsia-500/10 to-white/5 backdrop-blur p-5 shadow-[0_0_28px_rgba(217,70,239,0.12)]">
              <p className="text-[11px] uppercase tracking-[0.22em] text-slate-300">
                Résumé mission
              </p>

              <div className="mt-4 space-y-3 text-sm text-slate-200">
                <div className="rounded-2xl border border-white/10 bg-black/30 p-4">
                  <p className="text-[11px] uppercase tracking-[0.18em] text-slate-400">
                    Quêtes secondaires
                  </p>

                  <div className="mt-3 grid grid-cols-2 gap-2">
                    {gameStats?.specialStats?.map(stat => (
                      <div key={stat.id} className="rounded-2xl border border-white/10 bg-white/5 p-3">
                        <p className="text-[11px] text-slate-400">{stat.label}</p>
                        <p className={`mt-1 text-xl font-extrabold ${stat.color}`}>{stat.value}</p>
                        <p className="text-xs text-slate-400 mt-1">{stat.sub}</p>
                      </div>
                    ))}

                  </div>
                </div>

              </div>
            </div>

            <div className="rounded-3xl border border-white/10 bg-white/5 backdrop-blur p-5">
              <p className="text-[11px] uppercase tracking-[0.22em] text-slate-400">
                Liens
              </p>
              <div className="mt-3 flex flex-col gap-2">
                <Link
                  to="/scoreboards"
                  className="rounded-2xl px-4 py-2 text-sm font-semibold bg-white/5 border border-white/10 hover:bg-white/10 transition"
                >
                  Voir leaderboard global
                </Link>
              </div>
            </div>
            {/* Témoignages */}
            <div className="relative rounded-3xl p-5 border border-white/10 bg-white/5 backdrop-blur overflow-hidden
                shadow-[0_0_26px_rgba(34,211,238,0.14)]
                hover:shadow-[0_0_34px_rgba(217,70,239,0.18)]
                transition-shadow">
              {/* liseré néon */}
              <div className="pointer-events-none absolute inset-0 rounded-3xl">
                <div className="absolute -inset-[1px] rounded-3xl bg-gradient-to-br from-cyan-400/35 via-fuchsia-500/20 to-indigo-400/25 blur-[10px] opacity-60" />
                <div className="absolute inset-0 rounded-3xl border border-cyan-400/25" />
                {/* petit “sheen” léger */}
                <div className="absolute -top-20 -right-20 h-56 w-56 rounded-full bg-cyan-400/10 blur-2xl" />
              </div>

              <div className="relative">
                <p className="text-[13px] uppercase tracking-[0.22em] text-fuchsia-300">
                  Témoignage (optionnel)
                </p>

                {!tSent ? (
                  <>
                    <p className="mt-2 text-sm text-slate-300">
                      Un retour rapide nous aide beaucoup. Il sera{" "}
                      <span className="text-slate-200 font-semibold">validé par un admin</span>{" "}
                      avant d’apparaître sur la Home.
                    </p>

                    <div className="mt-4 space-y-3">
                      <input
                        value={tName}
                        onChange={(e) => setTName(e.target.value)}
                        placeholder="Prénom"
                        required
                        className="w-full rounded-xl bg-black/30 border border-white/10 px-3 py-2 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-fuchsia-400/40"
                      />

                      <input
                        value={tOrg}
                        onChange={(e) => setTOrg(e.target.value)}
                        required
                        placeholder="Rôle / entreprise — ex: RH • Startup Tech"
                        className="w-full rounded-xl bg-black/30 border border-white/10 px-3 py-2 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-fuchsia-400/40"
                      />

                      <select
                        value={tTag}
                        onChange={(e) => setTTag(e.target.value)}
                        className="w-full rounded-xl bg-black/30 border border-white/10 px-3 py-2 text-sm text-white focus:outline-none focus:ring-2 focus:ring-fuchsia-400/40"
                      >
                        <option>Onboarding</option>
                        <option>Hybride</option>
                        <option>Team building</option>
                        <option>Séminaire</option>
                        <option>Cohésion</option>
                      </select>

                      <div>
                        <p className="text-[11px] uppercase tracking-[0.22em] text-slate-400">
                          Note
                        </p>

                        <div className="mt-2 flex items-center gap-1">
                          {[1, 2, 3, 4, 5].map((n) => (
                            <button
                              key={n}
                              type="button"
                              onClick={() => setTRating(n)}
                              className={[
                                "h-9 w-9 rounded-xl border text-lg transition",
                                n <= tRating
                                  ? "border-orange-400/40 bg-orange-500/15"
                                  : "border-white/10 bg-black/30 hover:bg-white/5"
                              ].join(" ")}
                              aria-label={`${n} étoile${n > 1 ? "s" : ""}`}
                            >
                              <span className={n <= tRating ? "text-orange-400" : "text-slate-500"}>
                                ★
                              </span>
                            </button>
                          ))}
                        </div>

                        {tError && tRating === 0 && (
                          <p className="mt-2 text-[12px] text-rose-200">
                            Merci de choisir une note.
                          </p>
                        )}
                      </div>

                      <textarea
                        value={tQuote}
                        onChange={(e) => setTQuote(e.target.value)}
                        placeholder="Votre témoignage (10–400 caractères)…"
                        rows={4}
                        maxLength={400}
                        className="w-full rounded-xl bg-black/30 border border-white/10 px-3 py-2 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-fuchsia-400/40 resize-none"
                      />

                      <div className="flex items-center justify-between">
                        <p className="text-[11px] text-slate-500">
                          {tQuote.trim().length}/400
                        </p>

                        <button
                          type="button"
                          onClick={submitTestimonial}
                          disabled={!canSend}
                          className="rounded-2xl px-4 py-2 text-sm font-semibold bg-cyan-500/20 border border-cyan-400/25 hover:bg-fuchsia-500/30 transition disabled:opacity-50"
                        >
                          {tSending ? "Envoi..." : "Envoyer"}
                        </button>
                      </div>

                      {tError && (
                        <div className="rounded-xl border border-rose-400/25 bg-rose-500/10 px-3 py-2 text-sm text-rose-200">
                          {tError}
                        </div>
                      )}
                    </div>
                  </>
                ) : (
                  <div className="mt-3 rounded-2xl border border-emerald-400/25 bg-emerald-500/10 px-4 py-3">
                    <p className="text-sm text-emerald-200 font-semibold">
                      Merci 🙌
                    </p>
                    <p className="text-[12px] text-slate-300 mt-1">
                      Ton témoignage est bien enregistré et{" "}
                      <span className="text-slate-200 font-semibold">attend validation</span>.
                    </p>
                  </div>
                )}
              </div>
            </div>

          </aside>
        </div>
      </div>
    </div>
  );
}
